<template>
  <div class="sf-page">
    <top-header narrow
                v-bind:title="$t('import')" />

    <div class="sf-container sf-container--narrow">
      <v-card class="sf-card"
              flat>
        <v-progress-linear color="accent"
                           v-bind:active="loading"
                           v-bind:value="loadingPercent" />
        <v-card-text class="sf-card__body">
          <p class="mb-1">{{ currentActionText }}</p>
          <p v-if="loading">Verbleibende Gesamtzeit: <strong v-if="remainingTime != null">{{  remainingTimeFormatted }}</strong><strong v-else>Berechne…</strong></p>
          <v-alert class="mt-4 mb-0"
                   text
                   type="warning"
                   v-if="doPrices">
            Dieser Vorgang kann <strong>bis zu 2 Stunden</strong> dauern. Die Mengenvorgaben/Preise werden während des Imports möglicherweise nicht in der App angezeigt. Es wird daher empfohlen, ihn nicht im laufenden Messebetrieb durchzuführen.
          </v-alert>
          <v-alert class="mt-4 mb-0"
                   text
                   type="info"
                   v-if="externalDatabaseStatus && !externalDatabaseStatus.enabled">
            <strong>Datenimport nicht verfügbar.</strong> {{ externalDatabaseStatus.message }}
          </v-alert>
          <p class="accent--text mt-4 mb-0"><strong>Bitte lassen Sie dieses Fenster während des Importvorgangs geöffnet!</strong></p>
        </v-card-text>
        <v-divider />
        <v-card-text class="sf-card__body">
          <h3 class="sf-section-title">Umfang</h3>
          <v-checkbox class="mt-0"
                      color="primary"
                      v-model="doQuantites"
                      label="Mengen laden"
                      hide-details></v-checkbox>
          <v-checkbox color="primary"
                      v-model="doPrices"
                      label="Preise laden"
                      hide-details></v-checkbox>
          <v-checkbox class="mt-6"
                      color="primary"
                      v-model="checkbox"
                      label="Ich habe die Hinweise gelesen und verstanden."
                      hide-details></v-checkbox>
        </v-card-text>
        <v-card-text class="sf-card__body pt-0"
                     v-if="errorLog != ''">
          <v-alert class="mb-0"
                   text
                   type="error">
            <p class="mb-0" v-html="errorLog"></p>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="accent"
                 depressed
                 v-on:click="start"
                 v-bind:disabled="!checkbox || loading || !externalDatabaseEnabled">
            <v-icon left>ti-database-import</v-icon>
            Datenimport starten
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-card class="sf-card mt-6"
              flat>
        <v-progress-linear color="accent"
                           indeterminate
                           v-bind:active="csvLoading" />
        <v-card-text class="sf-card__body">
          <h3 class="sf-section-title">CSV-Import Kunden und Lieferanten</h3>
          <p class="mb-0">Format: UTF-8, Semikolon-getrennt, Spaltennamen laut Spezifikation.</p>
        </v-card-text>
        <v-divider />
        <v-card-text class="sf-card__body">
          <v-radio-group class="mt-0"
                         hide-details
                         row
                         v-model="csvKind">
            <v-radio color="primary"
                     label="Kunden"
                     value="customers" />
            <v-radio color="primary"
                     label="Lieferanten"
                     value="suppliers" />
          </v-radio-group>
          <v-file-input accept=".csv,text/csv"
                        class="mt-4"
                        hide-details="auto"
                        label="CSV-Datei"
                        outlined
                        dense
                        prepend-icon=""
                        prepend-inner-icon="ti-file-text"
                        show-size
                        v-model="csvFile" />
        </v-card-text>
        <v-card-text class="sf-card__body pt-0"
                     v-if="csvResult != null">
          <v-alert class="mb-0"
                   text
                   v-bind:type="csvResult.errors.length > 0 ? 'error' : 'success'">
            <template v-if="csvResult.errors.length > 0">
              <p class="mb-2"><strong>Die Datei wurde nicht importiert.</strong> {{ csvResult.errors.length }} Fehler:</p>
              <ul class="mb-0">
                <li v-for="(error, index) in csvResult.errors"
                    v-bind:key="index">{{ error }}</li>
              </ul>
            </template>
            <template v-else-if="csvResult.dryRun">
              <strong>Prüfung erfolgreich, es wurde noch nichts gespeichert.</strong>
              {{ csvResult.total }} Zeilen: {{ csvResult.created }} würden neu angelegt, {{ csvResult.updated }} aktualisiert.
              Erkannte Spalten: {{ csvResult.columns.join(', ') }}.
            </template>
            <template v-else>
              <strong>Import abgeschlossen.</strong>
              {{ csvResult.total }} Zeilen: {{ csvResult.created }} neu angelegt, {{ csvResult.updated }} aktualisiert.
            </template>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary"
                 text
                 v-on:click="runCsvImport(true)"
                 v-bind:disabled="csvFile == null || csvLoading">
            <v-icon left>ti-list-check</v-icon>
            Nur prüfen
          </v-btn>
          <v-btn color="accent"
                 depressed
                 v-on:click="runCsvImport(false)"
                 v-bind:disabled="csvFile == null || csvLoading">
            <v-icon left>ti-file-import</v-icon>
            Importieren
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-card class="sf-card mt-6"
              flat>
        <v-progress-linear color="accent"
                           indeterminate
                           v-bind:active="imagesLoading" />
        <v-card-text class="sf-card__body">
          <h3 class="sf-section-title">Artikelbilder</h3>
          <p class="mb-2">Artikelbilder sind an die Artikelnummer gebunden und bleiben beim Datenimport erhalten. Lieferanten laden sie über die Design-Seite hoch, Administratoren im Tab „Design“ des Lieferanten.</p>
          <p class="mb-0"><strong>Altbilder übernehmen</strong> liest einmalig die bisherigen statischen Bilder (<code>{artikelnr}_1.jpg</code>) aus dem Ordner <code>Storage/legacy-product-images</code> auf dem API-Server ein. Bereits vorhandene Bilder werden nicht überschrieben, der Vorgang kann wiederholt werden.</p>
        </v-card-text>
        <v-card-text class="sf-card__body pt-0"
                     v-if="legacyResult != null">
          <v-alert class="mb-0"
                   text
                   v-bind:type="legacyResult.errors.length > 0 ? 'warning' : 'success'">
            <p class="mb-1"><strong>{{ legacyResult.imported }}</strong> Bilder übernommen, <strong>{{ legacyResult.skipped }}</strong> übersprungen (bereits vorhanden).</p>
            <ul class="mb-0"
                v-if="legacyResult.errors.length > 0">
              <li v-for="(error, index) in legacyResult.errors"
                  v-bind:key="index">{{ error }}</li>
            </ul>
          </v-alert>
        </v-card-text>
        <v-card-text class="sf-card__body pt-0"
                     v-if="orphanedImages != null">
          <v-alert class="mb-0"
                   text
                   v-bind:type="orphanedImages.length > 0 ? 'info' : 'success'">
            <template v-if="orphanedImages.length > 0">
              <p class="mb-1"><strong>{{ orphanedImages.length }}</strong> verwaiste Bilder (Artikelnummer nicht mehr vorhanden): {{ orphanedImages.map(i => i.productId).join(', ') }}</p>
              <p class="mb-0">Verwaiste Bilder werden wieder sichtbar, sobald ein Artikel mit dieser Nummer importiert wird.</p>
            </template>
            <template v-else>Keine verwaisten Bilder.</template>
          </v-alert>
        </v-card-text>
        <v-card-text class="sf-card__body pt-0"
                     v-if="imagesError != null">
          <v-alert class="mb-0"
                   text
                   type="error">{{ imagesError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary"
                 text
                 v-on:click="checkOrphanedImages"
                 v-bind:disabled="imagesLoading">
            <v-icon left>ti-photo-search</v-icon>
            Verwaiste Bilder prüfen
          </v-btn>
          <v-btn color="error"
                 text
                 v-on:click="removeOrphanedImages"
                 v-bind:disabled="imagesLoading"
                 v-if="orphanedImages != null && orphanedImages.length > 0">
            <v-icon left>ti-trash</v-icon>
            Verwaiste löschen
          </v-btn>
          <v-spacer />
          <v-btn color="accent"
                 depressed
                 v-on:click="runLegacyImport"
                 v-bind:disabled="imagesLoading">
            <v-icon left>ti-photo-up</v-icon>
            Altbilder übernehmen
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';

  //import AttendanceCard from '@/components/administration/AttendanceCard'
  import TopHeader from '@/components/common/TopHeader'

  export default {
    components: {
      //AttendanceCard,
      TopHeader
    },
    computed: {
      // Unbekannter Status (z. B. Statusabfrage fehlgeschlagen) blockiert nicht: der Server antwortet dann mit 503 und der Ablauf bricht ab.
      externalDatabaseEnabled() {
        return this.externalDatabaseStatus == null || this.externalDatabaseStatus.enabled
      },
      remainingTimeFormatted() {
        const minutes = Math.floor(this.remainingTime / 60)
        const seconds = Math.floor(this.remainingTime % 60)

        return `${minutes} Minuten und ${seconds} Sekunden`
      },

      ...mapGetters({
        suppliers: 'suppliers',
      })
    },
    async created() {
      try {
        this.externalDatabaseStatus = await this.loadExternalDatabaseStatus()
      } catch (e) {
        this.externalDatabaseStatus = null
      }
    },
    data: () => ({
      currentActionText: 'Bitte starten Sie den Datenimport.',
      loading: false,
      loadingPercent: 0,
      remainingTime: null,
      errorLog: '',
      externalDatabaseStatus: null,
      checkbox: false,
      doQuantites: false,
      doPrices: false,
      csvKind: 'customers',
      csvFile: null,
      csvLoading: false,
      csvResult: null,
      imagesError: null,
      imagesLoading: false,
      legacyResult: null,
      orphanedImages: null
    }),
    methods: {
      async start() {
        this.errorLog = ''
        this.loading = true

        try {
          await this.runImport()
        } catch (e) {
          // Z. B. 503, wenn die externe Sagasser-Datenbank in dieser Umgebung deaktiviert ist.
          this.errorLog += `Der Datenimport wurde abgebrochen: ${e.message}`
          this.currentActionText = 'Datenimport abgebrochen.'
          this.loading = false
          this.remainingTime = null
        }
      },

      async runImport() {

        let index = 1
        let avgSecondsPerSupplier = 50

        this.currentActionText = 'Lieferanten werden geladen…'
        await this.loadSuppliers()
        this.loadingPercent = 2

        this.remainingTime = 10

        if (this.doQuantites) {
          this.remainingTime += 30
        }

        if (this.doPrices) {
          this.remainingTime += this.suppliers.length * avgSecondsPerSupplier
        }

        this.currentActionText = 'Artikel-Basisdaten werden importiert…'
        await this.importProducts()
        this.loadingPercent = 4

        this.remainingTime -= 10

        if (this.doQuantites) {
          this.currentActionText = 'Bestehende Mengen werden gelöscht…'
          await this.clearQuantities()
          this.loadingPercent = 6

          this.currentActionText = 'Mengenvorgaben werden geladen…'
          await this.importQuantities()
          this.loadingPercent = 8

          this.remainingTime -= 30
        }

        if (this.doPrices) {
          this.currentActionText = 'Bestehende Preise werden gelöscht…'
          await this.clearPrices()
          this.loadingPercent = 10

          for (const supplier of this.suppliers) {
            const start = Date.now()

            this.currentActionText = `Preise für ${supplier.companyName} (${supplier.id}) werden importiert… (${index}/${this.suppliers.length})`

            try {
              await this.importPrices({ supplierId: supplier.id })
            } catch (e) {
              // Externe Datenbank nicht verfügbar: nicht je Lieferant weiterprobieren, sondern ganz abbrechen.
              if (e.status === 503) {
                throw e
              }

              this.errorLog += `Fehler beim Import der Preise für <strong>${supplier.companyName} (${supplier.id})</strong>. (Kundennr./Artikelnr.-Kombination möglicherweise nicht eindeutig.)<br>`
            }

            const end = Date.now()

            this.loadingPercent = 10 + (90 / this.suppliers.length) * index
            avgSecondsPerSupplier = ((end - start) / 1000 + avgSecondsPerSupplier) / 2

            this.remainingTime = (this.suppliers.length - index) * avgSecondsPerSupplier

            index++
          }
        }

        this.loadingPercent = 100

        this.currentActionText = 'Datenimport erfolgreich abgeschlossen.'

        this.loading = false
        this.remainingTime = null
      },

      async runCsvImport(dryRun) {
        if (this.csvFile == null) {
          return
        }

        this.csvLoading = true
        this.csvResult = null

        try {
          const action = this.csvKind === 'suppliers' ? this.importSuppliersCsv : this.importCustomersCsv

          this.csvResult = await action({ file: this.csvFile, dryRun })
        } catch (e) {
          this.csvResult = { dryRun, total: 0, created: 0, updated: 0, columns: [], errors: [`Der Import ist fehlgeschlagen: ${e.message}`] }
        } finally {
          this.csvLoading = false
        }
      },

      async runLegacyImport() {
        this.imagesLoading = true
        this.imagesError = null
        this.legacyResult = null

        try {
          this.legacyResult = await this.importLegacyProductImages()
        } catch (e) {
          this.imagesError = `Die Übernahme ist fehlgeschlagen: ${e.message}`
        } finally {
          this.imagesLoading = false
        }
      },

      async checkOrphanedImages() {
        this.imagesLoading = true
        this.imagesError = null

        try {
          this.orphanedImages = await this.loadOrphanedProductImages()
        } catch (e) {
          this.imagesError = `Die Prüfung ist fehlgeschlagen: ${e.message}`
        } finally {
          this.imagesLoading = false
        }
      },

      async removeOrphanedImages() {
        this.imagesLoading = true
        this.imagesError = null

        try {
          await this.deleteOrphanedProductImages()
          this.orphanedImages = []
        } catch (e) {
          this.imagesError = `Das Löschen ist fehlgeschlagen: ${e.message}`
        } finally {
          this.imagesLoading = false
        }
      },

      ...mapActions({
        loadSuppliers: 'loadSuppliers',
        importLegacyProductImages: 'importLegacyProductImages',
        loadOrphanedProductImages: 'loadOrphanedProductImages',
        deleteOrphanedProductImages: 'deleteOrphanedProductImages',
        importCustomersCsv: 'importCustomersCsv',
        importSuppliersCsv: 'importSuppliersCsv',
        loadExternalDatabaseStatus: 'loadExternalDatabaseStatus',
        importProducts: 'importProducts',
        importPrices: 'importPrices',
        clearPrices: 'clearPrices',
        importQuantities: 'importQuantities',
        clearQuantities: 'clearQuantities'
      })
    },
    name: 'Import'
  }
</script>

<i18n>
{
  "de": {
    "import": "Datenimport"
  },
  "en": {
    "import": "Import"
  }
}
</i18n>